import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { DividendFrequency, DividendPayoutType } from '../../../hooks/dividend/dividend.types';
import { ShareClass } from '../../../types/share-classes.types';
import { getTranslation } from '../../../utils/get-translation.util';

/** Shared by the form's items and a saved payout's items — both carry these two ids. */
type SummaryItem = { shareClassId?: null | string; stakeholderId?: null | string };

const PERIOD_LABELS: Record<DividendFrequency, (index: null | number) => string> = {
  [DividendFrequency.ANNUALLY]: () => 'Annual',
  [DividendFrequency.MONTHLY]: (i) =>
    i ? new Date(0, i - 1).toLocaleString('en-US', { month: 'long' }) : '',
  [DividendFrequency.QUARTERLY]: (i) => (i ? `Q${i}` : ''),
  [DividendFrequency.SEMI_ANNUALLY]: (i) => (i === 1 ? 'H1' : i === 2 ? 'H2' : ''),
};

/** "2026 Q3" — year first, matching the Period Covered row in the designs. */
export const getPeriodCovered = ({
  frequency,
  periodIndex,
  year,
}: {
  frequency?: DividendFrequency;
  periodIndex?: null | number;
  year?: number;
}): string => {
  const periodLabel = frequency ? PERIOD_LABELS[frequency](periodIndex ?? null) : '';
  return [year, periodLabel].filter(Boolean).join(' ');
};

// Per-share-class payouts reach stakeholders through the classes they hold, so the count comes
// from the classes' holders; per-stakeholder payouts name their stakeholders directly.
export const countImpactedStakeholders = ({
  items,
  shareClasses,
  type,
}: {
  items: SummaryItem[];
  shareClasses: ShareClass[];
  type?: DividendPayoutType;
}): number => {
  if (type !== DividendPayoutType.PER_SHARE_CLASS) {
    return new Set(items.map((item) => item.stakeholderId).filter(Boolean)).size;
  }

  return new Set(
    shareClasses
      .filter((shareClass) => items.some((item) => item.shareClassId === shareClass.id))
      .flatMap((shareClass) => shareClass.stakeholderIds ?? []),
  ).size;
};

export const countShareClasses = (items: SummaryItem[]): number =>
  new Set(items.map((item) => item.shareClassId).filter(Boolean)).size;

/** Stats card + "Review →" strip, shared by the review step and the read-only details view. */
export const SummaryHighlights: FC<{
  impactedStakeholders: number;
  onReview: () => void;
  totalPayout: string;
}> = ({ impactedStakeholders, onReview, totalPayout }) => (
  <div className="flex flex-col gap-3 rounded-md bg-brand-25 px-0.5 pb-3 pt-0.5">
    <div className="overflow-hidden rounded-md bg-white shadow-[0px_-1px_3px_0px_rgba(16,24,40,0.04),0px_2px_3px_0px_rgba(16,24,40,0.1),0px_1px_2px_0px_rgba(16,24,40,0.06)]">
      <div className="flex h-12 items-center justify-between border-b border-gray-100 px-4">
        <span className="text-sm font-[450] text-gray-700">
          {getTranslation('IMPACTED_STAKEHOLDERS')}
        </span>
        <span className="text-sm font-[450] text-gray-700">{impactedStakeholders}</span>
      </div>
      <div className="flex h-12 items-center justify-between px-4">
        <span className="text-sm font-[450] text-gray-700">{getTranslation('TOTAL_PAYOUT')}</span>
        <span className="text-sm font-[550] text-forest-600">{totalPayout}</span>
      </div>
    </div>
    <button className="flex w-fit items-center gap-2 px-3.5" onClick={onReview} type="button">
      <span className="text-sm font-[450] text-brand-700">{getTranslation('REVIEW')}</span>
      <svg className="size-5" fill="none" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M4.167 10h11.666M15.833 10l-5-5M15.833 10l-5 5"
          stroke="#2565C8"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="1.5"
        />
      </svg>
    </button>
  </div>
);

export const SummaryRow: FC<{
  /** Document rows label their files without one (design 4937:31921). */
  hideColon?: boolean;
  highlight?: boolean;
  label: string;
  value: ReactNode;
}> = ({ highlight, hideColon, label, value }) => (
  <div className="flex items-start gap-4 px-4 py-3">
    {/* Labels carry a trailing colon in the design (86cayg7c8). */}
    <span className="w-[108px] shrink-0 text-xs font-[450] leading-4 text-gray-500">
      {hideColon ? label : `${label}:`}
    </span>
    <span
      className={twMerge(
        'min-w-0 flex-1 break-words text-xs font-[450] leading-4 text-gray-700',
        highlight && 'text-forest-600',
      )}
      title={typeof value === 'string' ? value : undefined}
    >
      {value}
    </span>
  </div>
);

export const SummarySection: FC<{
  /** Rendered at the right of the header — the edit summary puts its pencil there. */
  action?: ReactNode;
  children: ReactNode;
  strong?: boolean;
  title: string;
}> = ({ action, children, strong, title }) => (
  <div className="shrink-0 overflow-hidden rounded-lg border border-gray-100 bg-white">
    <div className="flex items-center justify-between gap-2 bg-gray-50 px-4 py-2">
      <span
        className={twMerge(
          'block min-w-0 flex-1 truncate text-sm text-gray-700',
          strong ? 'font-[550]' : 'font-medium',
        )}
        title={title}
      >
        {title}
      </span>
      {action}
    </div>
    <div className="flex flex-col divide-y divide-gray-100">{children}</div>
  </div>
);
